/**
 * queries.ts – rewritten for Django REST API backend
 * --------------------------------------------------
 * Replaces Drizzle/Postgres logic with HTTP fetch calls
 * to our Django endpoints (https://api-dggs.hartis.org)
 *
 * Works with JWT-based authentication (simplejwt).
 */

import { apiFetch, ApiError } from "@/lib/utils"
import type { Chat, Message } from "@/lib/types"

/** ------------------------------------------------------------------------
 *  CHATS
 * ------------------------------------------------------------------------ */

/**
 * Get all chats for current user
 */
export async function getChats(limit = 50, offset = 0) {
  return apiFetch(`/api/chats/?limit=${limit}&offset=${offset}`)
}

/**
 * Get single chat by ID
 * Note: Using object param to match expected signature
 */
export async function getChatById({ id }: { id: string }) {
  try {
    return await apiFetch(`/api/chats/${id}/`)
  } catch (error) {
    // IF it is a 404, swallow the error and return null
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    // IF it is any other error (500, 403, etc), let it crash the page
    throw error
  }
}

/**
 * Alias for getChatById (for consistency)
 */
export async function getChat(chatId: string) {
  return getChatById({ id: chatId })
}

/**
 * Create/save a new chat
 * Matches the signature expected by the API route
 */
export async function saveChat({ id, title, visibility }) {
  return apiFetch(`/api/chats/`, {
    method: "POST",
    body: JSON.stringify({ id, title, visibility }),
  })
}

/**
 * Legacy create chat method (kept for compatibility)
 */
export async function createChat(title: string, visibility = "private") {
  return apiFetch(`/api/chats/`, {
    method: "POST",
    body: JSON.stringify({ title, visibility }),
  })
}

/**
 * Delete chat by ID
 * Note: Using object param to match expected signature
 */
export async function deleteChatById({ id }: { id: string }) {
  return apiFetch(`/api/chats/${id}/`, {
    method: "DELETE",
  })
}

/**
 * Alias for deleteChatById
 */
export async function deleteChat(chatId: string) {
  return deleteChatById({ id: chatId })
}

/**
 * Update chat visibility
 */
export async function updateChatVisiblityById({
  chatId,
  visibility,
}: {
  chatId: string
  visibility: string
}) {
  return apiFetch(`/api/chats/${chatId}/`, {
    method: "PATCH",
    body: JSON.stringify({ visibility }),
  })
}

/**
 * Update chat's last context (for usage tracking)
 */
export async function updateChatLastContextById({
  chatId,
  context,
}: {
  chatId: string
  context: any
}) {
  return apiFetch(`/api/chats/${chatId}/context/`, {
    method: "PATCH",
    body: JSON.stringify({ last_context: context }),
  })
}

/** ------------------------------------------------------------------------
 *  MESSAGES
 * ------------------------------------------------------------------------ */

/**
 * Get all messages for a chat
 * Note: Using object param to match expected signature
 */
export async function getMessagesByChatId({ id }: { id: string }) {
  return apiFetch(`/api/messages/?chat=${id}`)
}

/**
 * Get a single message by ID
 */
export async function getMessageById({ id }: { id: string }) {
  return apiFetch(`/api/messages/${id}/`)
}

/**
 * Create a single message
 */
export async function createMessage(
  chatId: string,
  role: string,
  content: string,
  attachments: any = null
) {
  return apiFetch("/api/messages/", {
    method: "POST",
    body: JSON.stringify({ 
      chat: chatId, 
      role, 
      content, 
      attachments 
    }),
  })
}

/**
 * Bulk save messages
 * This is what the streaming API route uses
 */
export async function saveMessages({
  messages,
}: {
  messages: Array<{
    chatId: string
    id: string
    role: string
    parts: any[]
    attachments: any[]
    createdAt: Date
  }>
}) {
  return apiFetch("/api/messages/bulk/", {
    method: "POST",
    body: JSON.stringify({ messages }),
  })
}

/**
 * Get message count for rate limiting
 */
export async function getMessageCount({
  differenceInHours,
}: {
  differenceInHours: number
}) {
  const response = await apiFetch(`/api/messages/count/?hours=${differenceInHours}`)
  return response.count || 0
}

/**
 * Delete all messages in a chat created after a given timestamp
 */
export async function deleteMessagesByChatIdAfterTimestamp({
  chatId,
  timestamp,
}: {
  chatId: string
  timestamp: string
}) {
  return apiFetch(`/api/messages/cleanup/`, {
    method: "POST",
    body: JSON.stringify({ chat: chatId, after: timestamp }),
  })
}

/** ------------------------------------------------------------------------
 *  STREAMS (Optional - for resumable streams)
 * ------------------------------------------------------------------------ */

/**
 * Create a stream ID for resumable streaming
 * Currently not implemented in Django - you can stub this or implement later
 */
export async function createStreamId({
  streamId,
  chatId,
}: {
  streamId: string
  chatId: string
}) {
  return apiFetch(`/api/streams/`, {
    method: "POST",
    body: JSON.stringify({ id: streamId, chatId }),
  })
}

export async function getStreamIdsByChatId({
  chatId,
}: {
  chatId: string
}) {
  return apiFetch(`/api/streams/?chatId=${chatId}`)
}


/** ------------------------------------------------------------------------
 *  LLM (LLaMA / Ollama) RESPONSE GENERATION
 * ------------------------------------------------------------------------ */

/**
 * Request LLM response from backend.
 * The backend handles model routing and message persistence.
 */
export async function generateChatResponse({
  chatId,
  messages,
  model,
  visibility,
}: {
  chatId: string
  messages: { role: string; content: string }[]
  model?: string
  visibility?: string
}) {
  return apiFetch("/api/chat/ask/", {
    method: "POST",
    body: JSON.stringify({
      chat_id: chatId,
      messages,
      model,
      visibility,
    }),
  })
}


/** ------------------------------------------------------------------------
 *  VOTES
 * ------------------------------------------------------------------------ */

/**
 * Create or update a vote
 */ 
export async function voteMessage({
  chatId,
  messageId,
  type,
}: {
  chatId: string
  messageId: string
  type: "up" | "down"
}) {
  try {
    // Check if a vote already exists for this message (by current user)
    const existing = await apiFetch(`/api/votes/?chat=${chatId}&message=${messageId}`)

    if (Array.isArray(existing) && existing.length > 0) {
      const vote = existing[0]
      // Update existing vote
      return await apiFetch(`/api/votes/${vote.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_upvoted: type === "up" }),
      })
    } else {
      // Create a new vote
      return await apiFetch(`/api/votes/`, {
        method: "POST",
        body: JSON.stringify({
          chat: chatId,
          message: messageId,
          is_upvoted: type === "up",
        }),
      })
    }
  } catch (error) {
    console.error("voteMessage error", error)
    throw new Error("Failed to vote message")
  }
}

/**
 * Get votes for all messages in a chat
 */
export async function getVotesByChatId({ id }: { id: string }) {
  const res = await apiFetch(`/api/votes/?chat_id=${id}`)
  // Handle paginated or non-paginated response
  return Array.isArray(res) ? res : res.results || []
}


/** ------------------------------------------------------------------------
 *  MAP ARTIFACTS
 * ------------------------------------------------------------------------ */

export async function fetchMapArtifacts(chatId: string) {
  return apiFetch(`/api/chats/${chatId}/artifacts/`)
}


/** ------------------------------------------------------------------------
 *  GEODATA
 * ------------------------------------------------------------------------ */

export async function fetchAdministrativeData() {
  return apiFetch(`/api/administrative/geojson/`)
}


/** ------------------------------------------------------------------------
 *  PLACEHOLDERS (disabled for now)
 * ------------------------------------------------------------------------ */

// Votes, Documents, Suggestions – skipped for DGGS MVP
export const createVote = async () => null
export const createDocument = async () => null
export const saveDocument = async () => null
export const getDocumentById = async () => null
export const saveSuggestions = async () => null
export const createSuggestion = async () => null
export const getSuggestionsByDocumentId = async () => []